import React, { useEffect, useMemo, useRef } from 'react';
import { CommentType } from 'infrastructure/enums';
import { groupArrayByFieldname } from 'infrastructure/functions';
import InfiniteScroll from 'react-infinite-scroll-component';
import BaseLoader from 'components/atoms/base-loader';

import s from './styles.module.scss';
import GeneralCommentsListMessageItem from './message-item';
import GeneralCommentsListCallItem from './call-item';
import DateBlock from './date-block';

import type { Comment } from 'infrastructure/classes/comments/comment';

interface IGeneralCommentsListProps {
  id: string;
  items: Array<Comment>;
  height?: number;
  hasMore?: boolean;
  loadNext?: () => void;
  onDeleteComment: (comment: Comment) => Promise<void>;
}

const GeneralCommentsList: React.FC<IGeneralCommentsListProps> = (props) => {
  const {
    id,
    items,
    hasMore = false,
    height,
    loadNext,
    onDeleteComment,
  } = props;

  const groupedItems = useMemo(
    () => groupArrayByFieldname(items.reverse(), 'createdDate'),
    [items],
  );

  const scrollRef = useRef<HTMLDivElement | null>(null);
  const lastKnownDateRef = useRef<Date | null>(null);

  useEffect(() => {
    if (scrollRef.current && items.length > 0) {
      const dates = items.map((el) => new Date(el.createdAt));
      if (dates.length === 0) return;

      const newestDate = dates.reduce((latest, current) =>
        current > latest ? current : latest,
      );

      if (!lastKnownDateRef.current || newestDate > lastKnownDateRef.current) {
        scrollRef.current.scrollTo({
          top: 0,
          behavior: 'smooth',
        });

        lastKnownDateRef.current = newestDate;
      }
    }
  }, [items]);

  return (
    <div id={id} style={{ height }} className={s.list} ref={scrollRef}>
      <InfiniteScroll
        scrollableTarget={id}
        style={{
          overflow: 'hidden',
        }}
        dataLength={items.length}
        hasMore={hasMore}
        loader={<BaseLoader loading />}
        next={() => loadNext?.()}
        className={s['scroll-wrapper']}
      >
        {Object.keys(groupedItems).map((createdDate) => (
          <React.Fragment key={createdDate}>
            <DateBlock date={createdDate} />
            {groupedItems[createdDate].map((el) => (
              <React.Fragment key={el.key}>
                {el.type === CommentType.Text && (
                  <GeneralCommentsListMessageItem
                    data={el}
                    onDeleteComment={() => onDeleteComment(el)}
                  />
                )}
                {el.type === CommentType.Call && (
                  <GeneralCommentsListCallItem data={el} />
                )}
              </React.Fragment>
            ))}
          </React.Fragment>
        ))}
      </InfiniteScroll>
    </div>
  );
};

export default GeneralCommentsList;
